import type {
  Applicant,
  AssetCategory,
  AssetItem,
  Borrower,
  CaseData,
  CaseMeta,
  DebtCategory,
  DebtItem,
  Guarantor,
  IncomeCategory,
  IncomeItem,
  PropertyData,
} from "./caseSchema";

type UnknownRecord = Record<string, unknown>;

declare global {
  interface Window {
    BLIncomeContract?: {
      getPersonIncomeSources(input: unknown, role: "BORROWER" | "GUARANTOR"): Array<{
        type: string;
        normalizedMonthlyAmount: number | null;
        metadata?: UnknownRecord;
      }>;
    };
  }
}

type PersonSection = {
  incomes: IncomeItem[];
  assets: AssetItem[];
  debts: DebtItem[];
};

const CHINESE_DIGITS: Record<string, number> = {
  零: 0,
  一: 1,
  二: 2,
  兩: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

const SMALL_UNITS: Record<string, number> = {
  十: 10,
  百: 100,
  千: 1000,
};

function toObject(v: unknown): UnknownRecord {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as UnknownRecord)
    : {};
}

function toArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function toStringOrEmpty(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function containsChineseNumberTokens(s: string): boolean {
  return /[零一二兩三四五六七八九十百千]/.test(s);
}

function parseChineseUnder10000(segment: string): number {
  if (!segment) return 0;

  let total = 0;
  let currentDigit: number | null = null;

  for (let i = 0; i < segment.length; i++) {
    const ch = segment[i]!;

    if (ch in CHINESE_DIGITS) {
      currentDigit = CHINESE_DIGITS[ch]!;
      continue;
    }

    if (ch in SMALL_UNITS) {
      const unit = SMALL_UNITS[ch]!;
      const digit = currentDigit ?? 1;
      total += digit * unit;
      currentDigit = null;
      continue;
    }
  }

  if (currentDigit !== null) total += currentDigit;
  return total;
}

function parseChineseNumber(text: string): number {
  const s = text;
  if (!s) return 0;

  const yiParts = s.split("億");
  if (yiParts.length === 2) {
    return (
      parseChineseNumber(yiParts[0] ?? "") * 100_000_000 +
      parseChineseNumber(yiParts[1] ?? "")
    );
  }

  const wanParts = s.split("萬");
  if (wanParts.length === 2) {
    return (
      parseChineseUnder10000(wanParts[0] ?? "") * 10_000 +
      parseChineseUnder10000(wanParts[1] ?? "")
    );
  }

  return parseChineseUnder10000(s);
}

function normalizeAmountRaw(raw: string): string {
  return raw
    .trim()
    .replace(/[，,\s]/g, "")
    .replace(/整$/g, "")
    .replace(/^NT\$?/i, "")
    .replace(/^\$/g, "")
    .replace(/臺/g, "台");
}

function parseAmountInput(raw: string, opts: { defaultUnit: "wan" | "yuan" }): number {
  const normalized = normalizeAmountRaw(raw);
  if (!normalized) return 0;

  const hasComma = /[，,]/.test(raw);
  const hasWan = normalized.includes("萬");
  const hasYuan = /元$/.test(normalized);

  if (containsChineseNumberTokens(normalized)) {
    const n = parseChineseNumber(normalized.replace(/元$/g, ""));
    return Math.trunc(n);
  }

  let s = normalized;

  if (hasWan) {
    s = s.replace(/萬/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n * 10_000) : 0;
  }

  if (hasYuan) {
    s = s.replace(/元$/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }

  if (hasComma) {
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }

  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return opts.defaultUnit === "wan" ? Math.trunc(n * 10_000) : Math.trunc(n);
}

function parseMoneyToYuan(raw: unknown, defaultUnit: "wan" | "yuan" = "wan"): number {
  if (raw == null) return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.trunc(raw);

  const s = String(raw).trim();
  if (!s) return 0;

  try {
    return parseAmountInput(s, { defaultUnit });
  } catch {
    return 0;
  }
}

export function normalizeApplicant(input: unknown): Applicant {
  const obj = toObject(input);
  const applicantObj = toObject(obj["applicant"]);

  return {
    name: toStringOrEmpty(applicantObj["name"]) || undefined,
    idNo: toStringOrEmpty(applicantObj["idNo"]) || undefined,
    mobile: toStringOrEmpty(applicantObj["mobile"]) || undefined,
    raw: Object.keys(applicantObj).length ? applicantObj : undefined,
  };
}

export function normalizeIncomeItems(input: unknown): IncomeItem[] {
  const obj = toObject(input);
  const incomeRoot = toObject(obj["_income"]);
  const out: IncomeItem[] = [];

  ([
    "salary",
    "business",
    "profession",
    "otherinc",
  ] as IncomeCategory[]).forEach((cat) => {
    const items = toArray(incomeRoot[cat]);
    items.forEach((it) => {
      const itObj = toObject(it);
      out.push({
        category: cat,
        monthlyYuan: parseMoneyToYuan(itObj["monthly"], "wan"),
        raw: itObj,
      });
    });
  });

  return out;
}

function normalizeCanonicalIncomeItems(input: unknown, role: "BORROWER" | "GUARANTOR"): IncomeItem[] {
  const adapter = typeof window !== "undefined" ? window.BLIncomeContract : undefined;
  if (!adapter) return [];
  const category: Record<string, IncomeCategory> = {
    SALARY: "salary", BUSINESS: "business", PROFESSIONAL_PRACTICE: "profession", OTHER: "otherinc",
  };
  return adapter.getPersonIncomeSources(input, role).map((source) => ({
    category: category[source.type] || "otherinc",
    monthlyYuan: source.normalizedMonthlyAmount || 0,
    raw: source.metadata || {},
  }));
}

export function normalizeAssetItems(input: unknown): AssetItem[] {
  const obj = toObject(input);
  const out: AssetItem[] = [];

  (
    [
      "deposit",
      "fixed",
      "fund",
      "bond",
      "gold",
      "stock",
      "ins",
      "misc",
    ] as AssetCategory[]
  ).forEach((cat) => {
    const items = toArray(obj[cat]);
    items.forEach((it) => {
      const itObj = toObject(it);
      out.push({
        category: cat,
        amountYuan: parseMoneyToYuan(itObj["amount"], "wan"),
        raw: itObj,
      });
    });
  });

  // 名下其他不動產（Pragmatic: other-re）
  toArray(obj["other-re"]).forEach((it) => {
    const itObj = toObject(it);
    out.push({
      category: "real_estate",
      amountYuan: parseMoneyToYuan(itObj["amount"], "wan"),
      raw: itObj,
    });
  });

  // 本案估值（Pragmatic: _case.amt）
  const caseObj = toObject(obj["_case"]);
  if (caseObj["amt"] != null) {
    out.push({
      category: "real_estate",
      amountYuan: parseMoneyToYuan(caseObj["amt"], "wan"),
      raw: caseObj,
    });
  }

  return out;
}

export function normalizeDebtItems(input: unknown): DebtItem[] {
  const obj = toObject(input);
  const debtRoot = toObject(obj["_debt"]);
  const out: DebtItem[] = [];

  (
    [
      "mortgage",
      "carloan",
      "personal",
      "creditcard",
      "bizloan",
      "otherdebt",
    ] as DebtCategory[]
  ).forEach((cat) => {
    const items = toArray(debtRoot[cat]);
    items.forEach((it) => {
      const itObj = toObject(it);
      out.push({
        category: cat,
        balanceYuan: parseMoneyToYuan(itObj["balance"], "wan"),
        monthlyYuan: parseMoneyToYuan(itObj["monthly"], "wan"),
        raw: itObj,
      });
    });
  });

  return out;
}

export function normalizePropertyData(input: unknown): PropertyData {
  const obj = toObject(input);
  const prop = toObject(obj["property"]);

  const fullAddress =
    toStringOrEmpty(prop["fullAddress"]) ||
    toStringOrEmpty(prop["address"]) ||
    toStringOrEmpty(prop["addr"]);

  const estimatedValueYuan =
    prop["estimatedValueYuan"] != null
      ? parseMoneyToYuan(prop["estimatedValueYuan"], "yuan")
      : parseMoneyToYuan(prop["estimatedValue"], "wan");

  return {
    caseType: typeof prop["caseType"] === "string" ? prop["caseType"] : undefined,
    city: toStringOrEmpty(prop["city"]) || undefined,
    district: toStringOrEmpty(prop["district"]) || undefined,
    fullAddress: fullAddress || undefined,
    buildDate: toStringOrEmpty(prop["buildDate"]) || undefined,
    parking: typeof prop["parking"] === "string" ? prop["parking"] : undefined,
    nonbank: typeof prop["nonbank"] === "string" ? prop["nonbank"] : undefined,
    estimatedValueYuan: estimatedValueYuan || undefined,
    raw: Object.keys(prop).length ? prop : undefined,
  };
}

function normalizeMeta(input: unknown): CaseMeta {
  const obj = toObject(input);

  const guarantorState =
    typeof obj["_guarantorState"] === "string"
      ? (obj["_guarantorState"] as string)
      : typeof obj["guarantorState"] === "string"
        ? (obj["guarantorState"] as string)
        : undefined;

  const metaObj = toObject(obj["meta"]);

  return {
    guarantorState,
    schemaVersion: (metaObj["schemaVersion"] as number | string | undefined) ?? undefined,
    source: (metaObj["source"] as string | undefined) ?? undefined,
  };
}

function normalizePersonSection(rawPerson: unknown, rootInput?: unknown, role?: "BORROWER" | "GUARANTOR"): PersonSection {
  const obj = toObject(rawPerson);
  const canonical = rootInput && role ? normalizeCanonicalIncomeItems(rootInput, role) : [];
  return {
    incomes: canonical,
    assets: normalizeAssetItems(obj),
    debts: normalizeDebtItems(obj),
  };
}

/**
 * toCaseData(input)
 * 把舊表單/舊資料結構/零散 raw input 轉成正式 CaseData。
 */
export function toCaseData(input: unknown): CaseData {
  const obj = toObject(input);

  const borrowerRaw = obj["b"] ?? obj["borrower"];
  const guarantorRaw = obj["g"] ?? obj["guarantor"];

  const borrowerSection = normalizePersonSection(borrowerRaw, obj, "BORROWER") as Borrower;

  const guarantorObj = toObject(guarantorRaw);
  const hasGuarantorData =
    (typeof window !== "undefined" && window.BLIncomeContract && window.BLIncomeContract.getPersonIncomeSources(obj, "GUARANTOR").length > 0) ||
    Object.keys(guarantorObj).length > 0 ||
    toArray(guarantorObj["deposit"]).length > 0 ||
    toArray(toObject(guarantorObj["_debt"])["mortgage"]).length > 0;

  const guarantorSection = hasGuarantorData
    ? (normalizePersonSection(guarantorObj, obj, "GUARANTOR") as Guarantor)
    : undefined;

  return {
    applicant: normalizeApplicant(obj),
    property: normalizePropertyData(obj),
    borrower: borrowerSection,
    guarantor: guarantorSection,
    meta: normalizeMeta(obj),
    raw: obj,
  };
}

// ----------------------------
// 最小 mock input + console.log 驗證
// ----------------------------
const _mockInput = {
  applicant: { name: "王小明", mobile: "0912345678" },
  property: { caseType: "buy", city: "台北市", district: "大安區", fullAddress: "仁愛路四段 1 號" },
  _guarantorState: "not_created",
  b: {
    _income: {
      salary: [{ company: "ABC", monthly: "8.8" }],
      business: [],
      profession: [],
      otherinc: [],
    },
    deposit: [{ bank: "台灣銀行", amount: "500萬" }],
    _case: { amt: "2000萬" },
    _debt: {
      mortgage: [{ bank: "某銀行", balance: "1000萬", monthly: "3.2" }],
      carloan: [],
      personal: [],
      creditcard: [],
      bizloan: [],
      otherdebt: [],
    },
  },
};

console.log("[caseAdapter] toCaseData =>", toCaseData(_mockInput));
